import type {
  CompletionRateData,
  CompletionRatePayload,
} from '@api/survey_dashboard/utilities/helpers';
import {
  Accordion,
  Card,
  ChartPieSliceIcon,
  LoaderSkeleton,
  TableData,
  UserCheckIcon,
} from '@utilities';

import styles from './_index.module.scss';

export interface CompletionRateCardProps {
  data: CompletionRatePayload | null;
  isLoading: boolean;
}

const CompletionRateCard = ({ data, isLoading }: CompletionRateCardProps) => {
  const tableData = data?.data?.[0]?.table;
  const columns = tableData?.columns ?? [];
  const rows = tableData?.data ?? [];
  return (
    <Card
      as={Accordion}
      className={styles['completion-rate-card']}
      data-testid="completion-rate-card"
      label={<Header data={tableData?.data} isPending={isLoading} />}
    >
      {columns.length > 0 && (
        <TableData columns={columns} data={rows} isLoading={isLoading} {...tableData} />
      )}
    </Card>
  );
};

const Header = ({ data, isPending }: { data?: CompletionRateData[]; isPending: boolean }) => {
  const completedResponseCount = data?.reduce((acc, item) => acc + (item?.completes ?? 0), 0) ?? 0;
  const totalResponseCount = data?.reduce((acc, item) => acc + (item?.total ?? 0), 0) ?? 0;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
  });
  const completionRate = formatter.format(
    (completedResponseCount ?? 0) / (totalResponseCount || 1)
  );

  return (
    <div className={styles['completion-rate-card-header']}>
      <div className={styles['completion-rate-card-header-content']}>
        <ChartPieSliceIcon />
        <strong>Completed Responses</strong>{' '}
        {isPending ? <HeaderValueSkeleton width={36} /> : <span>{completedResponseCount}</span>}
      </div>
      <div className={styles['completion-rate-card-header-content']}>
        <UserCheckIcon />
        <strong>Completion Rate</strong>{' '}
        {isPending ? <HeaderValueSkeleton width={52} /> : <span>{completionRate}</span>}{' '}
        {isPending ? (
          <HeaderValueSkeleton
            className={styles['completion-rate-card-header-completed-response-count-skeleton']}
            width={180}
          />
        ) : (
          <span className={styles['completion-rate-card-header-completed-response-count']}>
            {`(${completedResponseCount} out of ${totalResponseCount} total responses)`}
          </span>
        )}
      </div>
    </div>
  );
};

const HeaderValueSkeleton = ({ className, width }: { className?: string; width: number }) => (
  <LoaderSkeleton className={className} height={20} width={width}>
    <rect height="20" rx="4" ry="4" width={width} x="0" y="0" />
  </LoaderSkeleton>
);

export default CompletionRateCard;
